import * as THREE from 'three';
import type { Input } from '../core/Input';
import type { AudioManager } from '../audio/AudioManager';
import type { PlayerController } from '../player/PlayerController';

const INTERACT_DIST = 1.85;
const FACING_DOT = Math.cos((50 * Math.PI) / 180);
const FLUSH_COOLDOWN = 3.2;
const LEVER_DOWN = 0.85;

interface ToiletRuntime {
  root: THREE.Object3D;
  lever: THREE.Object3D | null;
  coolT: number;
  leverT: number;
}

/** F-to-flush intact toilets. Destroyed cisterns stay dead. */
export class ToiletFlushSystem {
  private readonly toilets: ToiletRuntime[] = [];
  private readonly forward = new THREE.Vector3();
  private readonly to = new THREE.Vector3();
  private readonly projected = new THREE.Vector3();

  constructor(
    scene: THREE.Object3D,
    private readonly audio: AudioManager,
  ) {
    scene.traverse((o) => {
      if (o.userData?.interact !== 'toilet') return;
      this.toilets.push({
        root: o,
        lever: (o.userData.lever as THREE.Object3D | undefined) ?? null,
        coolT: 0,
        leverT: 0,
      });
    });
  }

  /** Returns true if F was used on a toilet this frame. */
  update(player: PlayerController, input: Input, dt: number, allowInteract = true): boolean {
    for (const t of this.toilets) {
      t.coolT = Math.max(0, t.coolT - dt);
      if (t.leverT > 0) {
        t.leverT = Math.max(0, t.leverT - dt);
        const u = 1 - t.leverT / 0.45;
        const dip = u < 0.35 ? u / 0.35 : 1 - (u - 0.35) / 0.65;
        if (t.lever) t.lever.rotation.z = -LEVER_DOWN * Math.max(0, dip);
      }
    }

    if (!allowInteract || !input.interactPressed) return false;
    const best = this.pick(player);
    if (!best) return false;
    this.flush(best);
    return true;
  }

  lookTip(
    player: PlayerController,
    camera: THREE.Camera,
    width: number,
    height: number,
  ): { label: string; x: number; y: number } | null {
    const t = this.pick(player);
    if (!t || width < 8 || height < 8) return null;
    t.root.getWorldPosition(this.projected);
    this.projected.y += 0.82;
    this.projected.project(camera);
    if (this.projected.z < -1 || this.projected.z > 1) return null;
    return {
      label: 'F - Flush',
      x: Math.min(width - 40, Math.max(40, (this.projected.x * 0.5 + 0.5) * width)),
      y: Math.min(height - 24, Math.max(22, (-this.projected.y * 0.5 + 0.5) * height)),
    };
  }

  private flush(t: ToiletRuntime): void {
    t.coolT = FLUSH_COOLDOWN;
    t.leverT = 0.45;
    t.root.getWorldPosition(this.to);
    this.to.y += 0.7;
    this.audio.playToiletFlush(this.to);
  }

  private pick(player: PlayerController): ToiletRuntime | null {
    const pos = player.body.position;
    this.forward.set(-Math.sin(player.yaw), 0, -Math.cos(player.yaw));
    let best: ToiletRuntime | null = null;
    let bestScore = Infinity;
    for (const t of this.toilets) {
      if (t.root.userData.destroyed) continue;
      if (t.coolT > 0) continue;
      t.root.getWorldPosition(this.to);
      if (Math.abs(this.to.y - pos.y) > 1.5) continue;
      this.to.y = pos.y;
      const dist = this.to.distanceTo(pos);
      if (dist > INTERACT_DIST) continue;
      this.to.sub(pos);
      if (this.to.lengthSq() > 1e-6) this.to.normalize();
      const facing = this.forward.dot(this.to);
      if (facing < FACING_DOT && dist > 0.85) continue;
      const score = dist - facing * 0.35;
      if (score < bestScore) {
        bestScore = score;
        best = t;
      }
    }
    return best;
  }
}
